import { type PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { convertSchemaFormsToDynamicFormStoreState } from './helpers';
import { useDynamicFormSchemaSyncer } from './hooks/use-schema-syncer';
import { MediaAttachment } from './media-upload/types';
import { DynamicFormStoreProvider, DynamicFormStoreState, useNonReactiveDynamicFormStore } from './store';
import type { SchemaForm } from './types';

interface DynamicFormProviderProps extends PropsWithChildren {
  schemaForms: SchemaForm[];
  initialValues?: Record<string, unknown>;
  /** Section to open on mount (e.g. a restored draft). Clamped to the available sections. */
  initialActiveSectionIndex?: number;
  /** Pre-committed attachments keyed by attachment id (e.g. media restored from a draft). */
  initialAttachments?: Record<string, MediaAttachment>;
}

type DynamicFormInitialState = Pick<
  DynamicFormStoreState,
  'formSections' | 'formFieldsMap' | 'conditionsMap' | 'activeSectionIndex' | 'attachments'
>;

export const DynamicFormProvider = ({
  children,
  schemaForms,
  initialValues,
  initialActiveSectionIndex = 0,
  initialAttachments,
}: DynamicFormProviderProps) => {
  const initalState = useMemo<DynamicFormInitialState>(
    () => {
      const state = convertSchemaFormsToDynamicFormStoreState(schemaForms, { values: initialValues });
      const lastSectionIndex = Math.max(0, state.formSections.length - 1);
      return {
        ...state,
        activeSectionIndex: Math.max(0, Math.min(initialActiveSectionIndex, lastSectionIndex)),
        attachments: initialAttachments ?? {},
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // syncer owns all schema updates after mount
  );

  return (
    <DynamicFormStoreProvider initialState={initalState}>
      <DynamicFormAttachmentCleanup />
      <DynamicFormSchemaSyncer schemaForms={schemaForms} />
      {children}
    </DynamicFormStoreProvider>
  );
};

const DynamicFormAttachmentCleanup = () => {
  const store = useNonReactiveDynamicFormStore();
  const pendingClear = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // StrictMode's simulated unmount keeps the store (and draft-restored attachments) alive and remounts
    // immediately, so defer the clear and cancel it on remount; only a real unmount lets it run.
    clearTimeout(pendingClear.current);
    return () => {
      pendingClear.current = setTimeout(() => store.getState().clearAttachments());
    };
  }, [store]);

  return null;
};

const DynamicFormSchemaSyncer = ({ schemaForms }: { schemaForms: SchemaForm[] }) => {
  useDynamicFormSchemaSyncer(schemaForms);
  return null;
};
